import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService, AlertType, ColumnConfig, FieldType, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalFieldConfig, ModalMode, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { VesselDeckModel } from '../../../../shared/models/vessel-deck.model';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../../environments/environment.local';
import { Validators } from '@angular/forms';
import { ApiService } from '@solcre-org/core';

import { SIX_DIGITS_PATTERN_VALIDATOR } from '../../../../shared/validators/pattern-validators';

@Component({
  selector: 'app-vessel-deck',
  imports: [CommonModule, TranslateModule, GenericTableComponent],
  templateUrl: './vessel-deck.html'
})
export class VesselDeck {

  private headerConfigurationService = inject(HeaderConfigurationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  vesselId = signal<number | null>(null);
  deck = signal<VesselDeckModel | null>(null);

  endpoint = computed(() => {
    return environment.API.VESSELS_DECKS_URI.replace(':id', this.vesselId()?.toString() ?? '');
  });

  columns = computed<ColumnConfig<VesselDeckModel>[]>(() => [
    {
      key: 'deckNumber',
      label: this.translateService.instant('vessels.decks.deck_number'),
    },
    {
      key: 'deckName',
      label: this.translateService.instant('vessels.decks.deck_name'),
    },
    {
      key: 'deckType',
      label: this.translateService.instant('vessels.decks.deck_type'),
    },
    {
      key: 'width',
      label: this.translateService.instant('vessels.decks.width'),
    },
    {
      key: 'length',
      label: this.translateService.instant('vessels.decks.length'),
    },
    {
      key: 'minimumHeight',
      label: this.translateService.instant('vessels.decks.minimum_height'),
    },
    {
      key: 'maximumHeight',
      label: this.translateService.instant('vessels.decks.maximum_height'),
    },
    {
      key: 'fixedHeight',
      label: this.translateService.instant('vessels.decks.fixed_height'),
    },
    {
      key: 'weightToleranceTons',
      label: this.translateService.instant('vessels.decks.weight_tolerance_tons'),
    },
  ]);

  modalFields = computed<ModalFieldConfig<VesselDeckModel>[]>(() => [
    {
      key: 'deckNumber',
      label: this.translateService.instant('vessels.decks.deck_number'),
      placeholder: this.translateService.instant('vessels.decks.deck_number_placeholder'),
      type: FieldType.NUMBER,
      readonly: false,
      validators: [Validators.required],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'deckName',
      label: this.translateService.instant('vessels.decks.deck_name'),
      placeholder: this.translateService.instant('vessels.decks.deck_name_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: [Validators.required],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'deckType',
      label: this.translateService.instant('vessels.decks.deck_type'),
      placeholder: this.translateService.instant('vessels.decks.deck_type_placeholder'),
      type: FieldType.SELECT,
      searchable: true,
      readonly: false,
      options: [
        { label: 'Underdeck', value: 'underdeck' },
        { label: 'Weather Deck', value: 'weather_deck' },
      ],
      validators: [Validators.required],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'currentStatus',
      label: this.translateService.instant('vessels.decks.currentStatus'),
      placeholder: this.translateService.instant('vessels.decks.currentStatus_placeholder'),
      type: FieldType.SELECT,
      searchable: true,
      options: [
        { label: 'Operational', value: 'operational' },
        { label: 'Non Operational', value: 'non_operational' },
      ],
      readonly: false,
      validators: [Validators.required],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
      }
    },
    {
      key: 'width',
      label: this.translateService.instant('vessels.decks.width'),
      placeholder: this.translateService.instant('vessels.decks.width_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: [
        Validators.required,
        SIX_DIGITS_PATTERN_VALIDATOR
      ],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      }
    },
    {
      key: 'length',
      label: this.translateService.instant('vessels.decks.length'),
      placeholder: this.translateService.instant('vessels.decks.length_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: [
        Validators.required,
        SIX_DIGITS_PATTERN_VALIDATOR
      ],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      }
    },
    {
      key: 'minimumHeight',
      label: this.translateService.instant('vessels.decks.minimum_height'),
      placeholder: this.translateService.instant('vessels.decks.minimum_height_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: (formValue: VesselDeckModel) => !formValue?.fixedHeight ? [Validators.required, SIX_DIGITS_PATTERN_VALIDATOR] : [SIX_DIGITS_PATTERN_VALIDATOR],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      }
    },
    {
      key: 'maximumHeight',
      label: this.translateService.instant('vessels.decks.maximum_height'),
      placeholder: this.translateService.instant('vessels.decks.maximum_height_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: (formValue: VesselDeckModel) => !formValue?.fixedHeight ? [Validators.required, SIX_DIGITS_PATTERN_VALIDATOR] : [SIX_DIGITS_PATTERN_VALIDATOR],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      }
    },
    {
      key: 'fixedHeight',
      label: this.translateService.instant('vessels.decks.fixed_height'),
      placeholder: this.translateService.instant('vessels.decks.fixed_height_placeholder'),
      type: FieldType.TEXT,
      validators: [SIX_DIGITS_PATTERN_VALIDATOR],
      errorMessages: {
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      },
      readonly: false,
    },
    {
      key: 'weightToleranceTons',
      label: this.translateService.instant('vessels.decks.weight_tolerance_tons'),
      placeholder: this.translateService.instant('vessels.decks.weight_tolerance_tons_placeholder'),
      type: FieldType.TEXT,
      readonly: false,
      validators: [Validators.required, SIX_DIGITS_PATTERN_VALIDATOR],
      errorMessages: {
        required: this.translateService.instant('validators.required'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 6, decimals: 2 }),
      }
    },
    {
      key: 'linearMeters',
      label: this.translateService.instant('vessels.decks.linear_meters'),
      placeholder: this.translateService.instant('vessels.decks.linear_meters_placeholder'),
      type: FieldType.NUMBER,
      readonly: true,
      dynamicValue: (formValue: VesselDeckModel) => this.recalculateLinearMeters(formValue)
    },
    {
      key: 'squareMeters',
      label: this.translateService.instant('vessels.decks.square_meters'),
      placeholder: this.translateService.instant('vessels.decks.square_meters_placeholder'),
      type: FieldType.NUMBER,
      readonly: false,
    },
    {
      key: 'accessibleFromSide',
      label: this.translateService.instant('vessels.decks.accessible_from_side'),
      placeholder: this.translateService.instant('vessels.decks.accessible_from_side_placeholder'),
      type: FieldType.SWITCH,
      readonly: false,
    },
    {
      key: 'accessibleFromStern',
      label: this.translateService.instant('vessels.decks.accessible_from_stern'),
      placeholder: this.translateService.instant('vessels.decks.accessible_from_stern_placeholder'),
      type: FieldType.SWITCH,
      readonly: false,
    }
  ] as any as ModalFieldConfig<VesselDeckModel>[]);

  actions = computed<TableActionConfig[]>(() => [
    {
      action: TableAction.VIEW,
      isExtra: false,
    },
    {
      action: TableAction.EDIT,
      isExtra: false,
    },
    {
      action: TableAction.DELETE,
      isExtra: true,
    },
    {
      action: TableAction.CREATE,
      isExtra: false,
    }
  ]);

  globalCustomActions = computed<GlobalAction<VesselDeckModel>[]>(() => [
    {
      action: 'goBackToGroups',
      label: this.translateService.instant('vessels.decks.back_to_vessels'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToGroups(),
      class: 'c-btn c-btn--stroked',
    }
  ]);

  modelFactory = (data: any) => new VesselDeckModel().fromJSON(data);

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint(), {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }

  constructor() {
    this.route.paramMap.subscribe((params: any) => {
      const id = params.get('id');
      if (id) {
        const vesselId = parseInt(id, 10);
        this.vesselId.set(vesselId);
      }
    });
  }

  goBackToGroups(): void {
    this.router.navigate(['/app/vessels']);
  }

  onModalData(data: VesselDeckModel): void {
    if (data) {
      data.id = data.id;
    }
  }

  private recalculateLinearMeters(formValue: VesselDeckModel) {
    const height = formValue.fixedHeight ?? formValue.maximumHeight;
    const parsedHeight = height ? parseFloat(height) : NaN;

    if (!isNaN(parsedHeight) && parsedHeight >= 4) {
      const width = formValue.width ? parseFloat(formValue.width) : 0;
      return Math.floor(width / 3);
    }

    return 0;
  }

}
